import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { Button } from "../../../../components/src/Button";
import { FundParseOutputFile } from "./fundParseTypes";
import { guessMimeType } from "./fundParseUtils";

interface DownloadFileModalProps {
    file: FundParseOutputFile | null;
    onClose: () => void;
    onDownload: (file: FundParseOutputFile) => void;
}

function handleBackdropPointerDown(event: React.MouseEvent<HTMLDivElement>, onClose: () => void): void {
    event.preventDefault();
    queueMicrotask(onClose);
}

function handleDownloadClick(
    event: React.MouseEvent<HTMLButtonElement>,
    file: FundParseOutputFile,
    onDownload: (file: FundParseOutputFile) => void,
    onClose: () => void
): void {
    event.preventDefault();
    event.stopPropagation();
    try {
        onDownload(file);
    } catch {
        // Always dismiss the modal even if the download helper throws.
    } finally {
        queueMicrotask(onClose);
    }
}

function handleCancelClick(event: React.MouseEvent<HTMLButtonElement>, onClose: () => void): void {
    event.preventDefault();
    event.stopPropagation();
    queueMicrotask(onClose);
}

export function DownloadFileModal({ file, onClose, onDownload }: DownloadFileModalProps): React.ReactElement | null {
    useEffect(() => {
        if (!file) {
            return;
        }

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("keydown", handleEscape);
        };
    }, [file, onClose]);

    return createPortal(
        file ? (
            <div role="dialog" aria-modal="true" aria-labelledby="fundparse-download-title">
                <div
                    className="fixed inset-0 bg-black/50 z-[2147483646]"
                    data-testid="fundparse-download-backdrop"
                    onMouseDown={(event) => handleBackdropPointerDown(event, onClose)}
                />
                <div
                    className="fixed top-1/2 left-1/2 z-[2147483647] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white p-6 shadow-xl"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                >
                    <h2 id="fundparse-download-title" className="mb-2 text-lg font-semibold text-gray-900">
                        Download output file
                    </h2>
                    <p className="mb-1 text-sm text-gray-700">
                        File: <strong>{file.fileName}</strong>
                    </p>
                    <p className="mb-4 text-sm text-gray-500">Type: {file.mimeType || guessMimeType(file.fileName)}</p>
                    <div className="flex justify-end gap-2">
                        <Button variant="secondary" size="sm" onClick={(event) => handleCancelClick(event, onClose)}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={(event) => handleDownloadClick(event, file, onDownload, onClose)}
                        >
                            Download
                        </Button>
                    </div>
                </div>
            </div>
        ) : null,
        document.body
    );
}
